import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { motion } from 'framer-motion';
import { GitCompare, Award } from 'lucide-react';

const VariantComparison = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [comparisonData, setComparisonData] = useState({ series: [], metric: 'answer_rate' });
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('answer_rate');
  const animationDuration = reducedMotion ? 0 : 0.6; // seconds for framer-motion

  const metrics = [
    { key: 'answer_rate', label: 'Answer Rate', unit: '%', desc: 'Calls connected vs dialed' },
    { key: 'aht', label: 'AHT', unit: 'ms', desc: 'Average Handle Time' },
    { key: 'fcr', label: 'FCR', unit: '%', desc: 'First Call Resolution' },
    { key: 'asr_accuracy', label: 'ASR Accuracy', unit: '%', desc: 'Speech recognition quality' }
  ];

  useEffect(() => {
    fetchComparisonData();
  }, [filters, selectedMetric]);

  const fetchComparisonData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/analytics/variant-comparison?metric=${selectedMetric}`
      );
      const result = await response.json();
      setComparisonData(result);
    } catch (error) {
      console.error('Error fetching variant comparison:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMetricColor = (metric) => {
    const colors = {
      answer_rate: '#C9A24A',   // Cyan
      aht: '#a855f7',           // Purple
      fcr: '#10b981',           // Emerald
      asr_accuracy: '#f59e0b'   // Amber
    };
    return colors[metric] || '#6b7280';
  };

  const getWinnerIndex = () => {
    if (!comparisonData.series || comparisonData.series.length === 0) return -1;
    
    // For AHT, lower is better; for others, higher is better
    const isLowerBetter = selectedMetric === 'aht';
    
    let winnerIdx = 0;
    let bestValue = comparisonData.series[0].value;
    
    comparisonData.series.forEach((item, idx) => {
      if (isLowerBetter) {
        if (item.value < bestValue) {
          bestValue = item.value;
          winnerIdx = idx;
        }
      } else {
        if (item.value > bestValue) {
          bestValue = item.value;
          winnerIdx = idx;
        }
      }
    });
    
    return winnerIdx;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const currentMetric = metrics.find(m => m.key === selectedMetric);
      
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl">
          <p className="text-sm font-semibold text-slate-200">
            {data.bot_variant} ({data.prompt_version})
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {currentMetric?.label}: {data.value.toFixed(1)}{currentMetric?.unit}
          </p>
          <p className="text-xs text-slate-500">
            Samples: {data.count}
          </p>
        </div>
      );
    }
    return null;
  };

  const winnerIdx = getWinnerIndex();

  return (
    <Card className="glass-card" data-testid="variant-comparison">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-ink-500" />
              Variant/Prompt Comparison
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              {metrics.find(m => m.key === selectedMetric)?.desc}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Metric Toggles */}
        <div className="flex flex-wrap gap-2 mb-6">
          {metrics.map(m => (
            <button
              key={m.key}
              onClick={() => setSelectedMetric(m.key)}
              className={`px-4 py-2 text-sm rounded-lg transition-all ${
                selectedMetric === m.key
                  ? 'bg-gold text-white shadow-lg'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
              data-testid={`metric-toggle-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-80">
            <div className="animate-pulse text-slate-400">Loading comparison data...</div>
          </div>
        ) : comparisonData.series.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No variant data available yet</p>
            <p className="text-xs mt-2">Create campaigns with bot_variant and prompt_version to see comparisons</p>
          </div>
        ) : (
          <motion.div
            key={selectedMetric}
            initial={reducedMotion ? {} : { opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: animationDuration, ease: 'easeInOut' }}
          >
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={comparisonData.series} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="bar-gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={getMetricColor(selectedMetric)} stopOpacity={0.9} />
                    <stop offset="95%" stopColor={getMetricColor(selectedMetric)} stopOpacity={0.6} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="bot_variant"
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#94a3b8"
                  style={{ fontSize: '12px' }}
                  label={{ 
                    value: `${metrics.find(m => m.key === selectedMetric)?.label} (${metrics.find(m => m.key === selectedMetric)?.unit})`, 
                    angle: -90, 
                    position: 'insideLeft', 
                    fill: '#94a3b8' 
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="value" 
                  fill="url(#bar-gradient)"
                  radius={[8, 8, 0, 0]}
                  animationDuration={reducedMotion ? 0 : 600}
                  animationEasing="ease-in-out"
                />
              </BarChart>
            </ResponsiveContainer>

            {/* Winner Badge */}
            {winnerIdx >= 0 && comparisonData.series.length > 1 && (
              <motion.div
                className="mt-4 p-4 bg-gradient-to-r from-amber-900/30 to-yellow-900/30 border border-amber-600/50 rounded-lg"
                initial={reducedMotion ? {} : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <div className="flex items-center gap-3">
                  <Award className="w-6 h-6 text-amber-400" />
                  <div>
                    <div className="text-sm font-semibold text-amber-300">
                      🏆 Winner: {comparisonData.series[winnerIdx].bot_variant} ({comparisonData.series[winnerIdx].prompt_version})
                    </div>
                    <div className="text-xs text-slate-400">
                      {metrics.find(m => m.key === selectedMetric)?.label}: {comparisonData.series[winnerIdx].value.toFixed(1)}{metrics.find(m => m.key === selectedMetric)?.unit}
                      {' '}• {comparisonData.series[winnerIdx].count} calls
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default VariantComparison;
